import { useMemo, useState } from 'react';
import {
  Plus, Trash2, ChevronRight, Circle, CircleDot, CheckCircle2,
  Search, CalendarClock, LayoutList, Flame, ChevronsDownUp, ChevronsUpDown,
} from 'lucide-react';
import { useProjects, useSaveProjects } from '@/hooks/useDashboard';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type { Project, ProjectTask } from '@/lib/types';

const STATUS = [
  { value: 'active', label: 'Actif' },
  { value: 'planned', label: 'À venir' },
  { value: 'paused', label: 'Pause' },
  { value: 'done', label: 'Terminé' },
];

// Même vocabulaire que l'écran legacy (src/dashboard/public/app.js) : l'état
// d'une tâche vit dans `status`, il n'y a pas de booléen `done`.
const TASK_STATUS = [
  { value: 'todo', label: 'À faire' },
  { value: 'doing', label: 'En cours' },
  { value: 'done', label: 'Fait' },
];

// Un clic sur la pastille fait avancer la tâche : à faire → en cours → fait.
const NEXT_STATUS: Record<string, string> = { todo: 'doing', doing: 'done', done: 'todo' };

// Au-delà, la liste d'un projet est repliée derrière un « + N autres » : sans ce
// plafond, un projet à 73 tâches écrase les dix autres.
const VISIBLE_TASKS = 10;

// Un projet actif tient à l'écran jusqu'à ce seuil ; au-delà il s'ouvre à la demande.
const AUTO_OPEN_MAX = 12;

function uid() { return Math.random().toString(36).slice(2, 9); }

function isOpenTask(t: ProjectTask) { return t.status !== 'done'; }

function startOfToday() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }

/** Nombre de jours calendaires d'ici l'échéance. Négatif = en retard. */
function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - startOfToday().getTime()) / 86_400_000);
}

function dueLabel(iso?: string | null): string {
  const n = daysUntil(iso);
  if (n === null) return '';
  if (n < 0) return `retard ${-n} j`;
  if (n === 0) return "aujourd'hui";
  if (n === 1) return 'demain';
  if (n <= 7) return `${n} j`;
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' }).format(new Date(iso!));
}

function dueTone(iso?: string | null): string {
  const n = daysUntil(iso);
  if (n === null) return 'text-muted-foreground/50';
  if (n < 0) return 'text-destructive';
  if (n <= 2) return 'text-warning';
  if (n <= 7) return 'text-foreground/70';
  return 'text-muted-foreground';
}

/** Tri d'affichage : en cours d'abord, puis à faire par échéance, les faites en dernier. */
function sortTasks(tasks: ProjectTask[]): ProjectTask[] {
  const rank: Record<string, number> = { doing: 0, todo: 1, done: 2 };
  return [...tasks].sort((a, b) => {
    const ra = rank[a.status ?? 'todo'] ?? 1;
    const rb = rank[b.status ?? 'todo'] ?? 1;
    if (ra !== rb) return ra - rb;
    const da = daysUntil(a.due_date);
    const db = daysUntil(b.due_date);
    if (da === null && db === null) return 0;
    if (da === null) return 1;      // sans date = après les datées
    if (db === null) return -1;
    return da - db;
  });
}

// ─── Bribes d'interface ────────────────────────────────────────────────────

/** Pastille d'état : un clic fait avancer la tâche, pas de menu à ouvrir. */
function StatusDot({ status, onCycle }: { status: string; onCycle: (next: string) => void }) {
  const cfg = status === 'done'
    ? { Icon: CheckCircle2, cls: 'text-success' }
    : status === 'doing'
      ? { Icon: CircleDot, cls: 'text-warning' }
      : { Icon: Circle, cls: 'text-muted-foreground/60 hover:text-foreground' };
  const label = TASK_STATUS.find((s) => s.value === status)?.label ?? 'À faire';
  return (
    <button
      type="button"
      onClick={() => onCycle(NEXT_STATUS[status] ?? 'doing')}
      className={cn('mt-0.5 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring', cfg.cls)}
      aria-label={`État : ${label}. Cliquer pour passer à l'état suivant.`}
    >
      <cfg.Icon className="h-4 w-4" />
    </button>
  );
}

/** Échéance affichée en clair, éditable au clic. */
function DueChip({ value, onChange }: { value?: string | null; onChange: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <Input
        type="date"
        defaultValue={value ?? ''}
        autoFocus
        onBlur={(e) => { setEditing(false); if (e.target.value !== (value ?? '')) onChange(e.target.value); }}
        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="h-6 w-[125px] shrink-0 px-1 text-xs"
        aria-label="Échéance de la tâche"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        'shrink-0 rounded px-1 text-xs tabular-nums transition-colors hover:bg-muted',
        value ? dueTone(value) : 'text-muted-foreground/40 opacity-0 group-hover:opacity-100'
      )}
      aria-label={value ? `Échéance ${value}, modifier` : 'Ajouter une échéance'}
    >
      {value ? dueLabel(value) : '+ date'}
    </button>
  );
}

/**
 * Titre de tâche : affiché en texte qui passe à la ligne (l'ancien <input>
 * permanent tronquait chaque libellé en plein mot), édité au clic.
 */
function EditableTitle({ value, done, onCommit }: { value: string; done: boolean; onCommit: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <Input
        defaultValue={value}
        autoFocus
        onBlur={(e) => { setEditing(false); if (e.target.value !== value) onCommit(e.target.value); }}
        onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="h-6 flex-1 px-1 text-sm"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        'w-full min-w-0 flex-1 rounded px-1 text-left text-sm leading-snug break-words hover:bg-muted/60',
        done && 'text-muted-foreground line-through'
      )}
      title="Cliquer pour modifier"
    >
      {value || <span className="text-muted-foreground/50">Sans titre</span>}
    </button>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export function Projects() {
  const { data, isLoading } = useProjects();
  const save = useSaveProjects();
  const [local, setLocal] = useState<Project[] | null>(null);

  const [view, setView] = useState<'projets' | 'echeances'>('projets');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDone, setShowDone] = useState(false);
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const projects = local ?? data ?? [];

  const update = (next: Project[]) => { setLocal(next); save.mutate(next); };

  const addProject = () => update([...projects, { id: uid(), name: 'Nouveau projet', status: 'active', tasks: [] }]);
  const removeProject = (id?: string) => update(projects.filter((p) => p.id !== id));
  const patchProject = (id: string | undefined, patch: Partial<Project>) =>
    update(projects.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const addTask = (pid?: string) =>
    patchProject(pid, { tasks: [...(projects.find((p) => p.id === pid)?.tasks ?? []), { id: uid(), title: 'Nouvelle tâche', status: 'todo' }] });
  const patchTask = (pid: string | undefined, tid: string | undefined, patch: Partial<ProjectTask>) =>
    patchProject(pid, { tasks: (projects.find((p) => p.id === pid)?.tasks ?? []).map((t) => (t.id === tid ? { ...t, ...patch } : t)) });
  const removeTask = (pid: string | undefined, tid: string | undefined) =>
    patchProject(pid, { tasks: (projects.find((p) => p.id === pid)?.tasks ?? []).filter((t) => t.id !== tid) });

  const q = query.toLowerCase().trim();

  /** Projets filtrés, chacun avec sa liste de tâches déjà filtrée et triée. */
  const view0 = useMemo(() => {
    return projects
      .filter((p) => statusFilter === 'all' || (p.status ?? 'active') === statusFilter)
      .map((p) => {
        const all = p.tasks ?? [];
        const matchProject = !q || (p.name ?? '').toLowerCase().includes(q) || (p.client ?? '').toLowerCase().includes(q);
        const tasks = sortTasks(
          all
            .filter((t) => showDone || isOpenTask(t))
            .filter((t) => !q || matchProject || (t.title ?? '').toLowerCase().includes(q))
        );
        return { project: p, tasks, total: all.length, done: all.filter((t) => !isOpenTask(t)).length, open: all.filter(isOpenTask).length, matchProject };
      })
      .filter((row) => !q || row.matchProject || row.tasks.length > 0);
  }, [projects, q, statusFilter, showDone]);

  const stats = useMemo(() => {
    const t = projects.flatMap((p) => p.tasks ?? []);
    const open = t.filter(isOpenTask);
    return {
      projects: projects.length,
      tasks: t.length,
      done: t.length - open.length,
      late: open.filter((x) => (daysUntil(x.due_date) ?? 99) < 0).length,
    };
  }, [projects]);

  /** Vue Échéances : toutes les tâches ouvertes à plat, groupées par urgence. */
  const agenda = useMemo(() => {
    const rows = view0.flatMap(({ project, tasks }) =>
      tasks.filter(isOpenTask).map((t) => ({ t, project }))
    );
    const buckets: Array<{ key: string; label: string; tone: string; items: typeof rows }> = [
      { key: 'late', label: 'En retard', tone: 'text-destructive', items: [] },
      { key: 'today', label: "Aujourd'hui", tone: 'text-warning', items: [] },
      { key: 'week', label: 'Dans les 7 jours', tone: 'text-foreground', items: [] },
      { key: 'later', label: 'Plus tard', tone: 'text-muted-foreground', items: [] },
      { key: 'none', label: 'Sans échéance', tone: 'text-muted-foreground', items: [] },
    ];
    for (const row of rows) {
      const n = daysUntil(row.t.due_date);
      const k = n === null ? 'none' : n < 0 ? 'late' : n === 0 ? 'today' : n <= 7 ? 'week' : 'later';
      buckets.find((b) => b.key === k)!.items.push(row);
    }
    for (const b of buckets) {
      b.items.sort((a, c) => (daysUntil(a.t.due_date) ?? 9e9) - (daysUntil(c.t.due_date) ?? 9e9));
    }
    return buckets.filter((b) => b.items.length > 0);
  }, [view0]);

  const isOpen = (p: Project, openCount: number) =>
    openMap[p.id ?? ''] ?? (Boolean(q) || ((p.status ?? 'active') === 'active' && openCount <= AUTO_OPEN_MAX));

  const setAll = (open: boolean) => {
    const next: Record<string, boolean> = {};
    projects.forEach((p) => { next[p.id ?? ''] = open; });
    setOpenMap(next);
  };

  if (isLoading) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-3">
      {/* Barre d'outils : ce qui rend 131 tâches lisibles tient ici. */}
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
          <TabsList>
            <TabsTrigger value="projets"><LayoutList className="mr-1 h-3.5 w-3.5" /> Par projet</TabsTrigger>
            <TabsTrigger value="echeances"><CalendarClock className="mr-1 h-3.5 w-3.5" /> Échéances</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filtrer projet ou tâche…"
            className="h-8 w-56 pl-7 text-sm"
            aria-label="Filtrer les projets et les tâches"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-32 text-xs" aria-label="Filtrer par état de projet"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les états</SelectItem>
            {STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button
          variant={showDone ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => setShowDone((v) => !v)}
          aria-pressed={showDone}
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Faites ({stats.done})
        </Button>

        {view === 'projets' && (
          <>
            <Button variant="ghost" size="icon-sm" onClick={() => setAll(true)} aria-label="Tout déplier" title="Tout déplier">
              <ChevronsUpDown className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={() => setAll(false)} aria-label="Tout replier" title="Tout replier">
              <ChevronsDownUp className="h-3.5 w-3.5" />
            </Button>
          </>
        )}

        <div className="ml-auto flex items-center gap-3">
          <div className="text-xs text-muted-foreground">
            <strong className="text-foreground num">{stats.projects}</strong> projets ·
            <strong className="text-foreground num"> {stats.tasks - stats.done}</strong> à faire
            {stats.late > 0 && <> · <strong className="text-destructive num">{stats.late}</strong> en retard</>}
          </div>
          <Button size="sm" onClick={addProject}><Plus className="h-4 w-4" /> Nouveau projet</Button>
        </div>
      </div>

      {/* ─── Vue Échéances ─────────────────────────────────────────────── */}
      {view === 'echeances' && (
        <div className="space-y-3">
          {agenda.map((b) => (
            <Card key={b.key}>
              <CardContent className="p-3">
                <div className="mb-2 flex items-center gap-2">
                  <span className={cn('text-sm font-semibold', b.tone)}>{b.label}</span>
                  <Badge variant="outline" className="text-[10px]">{b.items.length}</Badge>
                </div>
                <div className="space-y-0.5">
                  {b.items.map(({ t, project }) => (
                    <div key={`${project.id}-${t.id}`} className="group flex items-start gap-2 rounded px-1 py-0.5 hover:bg-muted/40">
                      <StatusDot status={t.status ?? 'todo'} onCycle={(v) => patchTask(project.id, t.id, { status: v })} />
                      {t.priority === 'high' && <Flame className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" aria-label="Priorité haute" />}
                      {/* Titre et projet empilés dans un bloc rétractable : côte à côte,
                          le nom de projet écrasait le titre à ~15 px sur mobile. */}
                      <div className="min-w-0 flex-1">
                        <EditableTitle value={t.title} done={false} onCommit={(v) => patchTask(project.id, t.id, { title: v })} />
                        <div className="truncate px-1 text-[11px] text-muted-foreground">{project.name}</div>
                      </div>
                      <DueChip value={t.due_date} onChange={(v) => patchTask(project.id, t.id, { due_date: v })} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          {agenda.length === 0 && (
            <Card><CardContent className="py-10 text-center text-muted-foreground">Aucune tâche ouverte pour ce filtre.</CardContent></Card>
          )}
        </div>
      )}

      {/* ─── Vue Par projet ────────────────────────────────────────────── */}
      {/* Colonnes CSS et non grille : une carte à 73 tâches ne doit pas
          imposer sa hauteur à la ligne entière (2 300 px de vide constatés). */}
      {view === 'projets' && (
        <div className="columns-1 gap-3 md:columns-2 xl:columns-3">
          {view0.map(({ project: p, tasks, total, done, open }) => {
            const opened = isOpen(p, open);
            const showAll = expanded[p.id ?? ''] ?? false;
            const shown = showAll ? tasks : tasks.slice(0, VISIBLE_TASKS);
            const nextDue = tasks.filter(isOpenTask).map((t) => t.due_date).find(Boolean);
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            const lateCount = tasks.filter((t) => isOpenTask(t) && (daysUntil(t.due_date) ?? 99) < 0).length;

            return (
              <Card key={p.id} className="mb-3 break-inside-avoid">
                {/* En-tête : lisible d'un coup d'œil même replié. */}
                <div className="flex items-center gap-1.5 border-b border-border/60 p-2">
                  <button
                    type="button"
                    onClick={() => setOpenMap((m) => ({ ...m, [p.id ?? '']: !opened }))}
                    className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-expanded={opened}
                    aria-label={opened ? `Replier ${p.name}` : `Déplier ${p.name}`}
                  >
                    <ChevronRight className={cn('h-4 w-4 transition-transform', opened && 'rotate-90')} />
                  </button>

                  <div className="min-w-0 flex-1">
                    <EditableTitle value={p.name} done={false} onCommit={(v) => patchProject(p.id, { name: v })} />
                    <div className="flex items-center gap-2 px-1 text-[11px] text-muted-foreground">
                      {p.client && <span className="truncate">{p.client}</span>}
                      <span className="num shrink-0">{done}/{total}</span>
                      {lateCount > 0 && <span className="shrink-0 text-destructive num">{lateCount} en retard</span>}
                      {lateCount === 0 && nextDue && <span className={cn('shrink-0', dueTone(nextDue))}>{dueLabel(nextDue)}</span>}
                    </div>
                  </div>

                  <Select value={p.status ?? 'active'} onValueChange={(v) => patchProject(p.id, { status: v })}>
                    <SelectTrigger className="h-7 w-[74px] shrink-0 text-[11px]" aria-label={`État du projet ${p.name}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon-sm" onClick={() => removeProject(p.id)} aria-label={`Supprimer le projet ${p.name}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {total > 0 && <Progress value={pct} className="h-0.5 rounded-none" />}

                {opened && (
                  <CardContent className="space-y-0.5 p-2">
                    {shown.map((t) => (
                      <div key={t.id} className="group flex items-start gap-2 rounded px-0.5 py-0.5 hover:bg-muted/40">
                        <StatusDot status={t.status ?? 'todo'} onCycle={(v) => patchTask(p.id, t.id, { status: v })} />
                        {t.priority === 'high' && t.status !== 'done' && (
                          <Flame className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" aria-label="Priorité haute" />
                        )}
                        <EditableTitle
                          value={t.title}
                          done={t.status === 'done'}
                          onCommit={(v) => patchTask(p.id, t.id, { title: v })}
                        />
                        <DueChip value={t.due_date} onChange={(v) => patchTask(p.id, t.id, { due_date: v })} />
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => removeTask(p.id, t.id)}
                          className="h-5 w-5 shrink-0 opacity-0 group-hover:opacity-100"
                          aria-label={`Supprimer la tâche ${t.title}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}

                    {tasks.length > VISIBLE_TASKS && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpanded((m) => ({ ...m, [p.id ?? '']: !showAll }))}
                        className="w-full justify-start text-xs text-muted-foreground"
                      >
                        {showAll ? 'Réduire la liste' : `+ ${tasks.length - VISIBLE_TASKS} autres`}
                      </Button>
                    )}

                    {tasks.length === 0 && (
                      <div className="px-1 py-1 text-xs text-muted-foreground">
                        {open === 0 && total > 0 ? 'Tout est fait.' : 'Aucune tâche.'}
                      </div>
                    )}

                    <Button variant="ghost" size="sm" onClick={() => addTask(p.id)} className="w-full justify-start text-xs">
                      <Plus className="h-3 w-3" /> Ajouter une tâche
                    </Button>
                  </CardContent>
                )}
              </Card>
            );
          })}

          {view0.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center gap-3 py-10 text-muted-foreground">
                {projects.length === 0 ? 'Aucun projet pour le moment' : 'Aucun projet ne correspond au filtre'}
                {projects.length === 0 && <Button onClick={addProject}><Plus className="h-4 w-4" /> Créer le premier projet</Button>}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
